import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import {
  AddIcon,
  AsyncSelect,
  Button,
  FlexContent,
  Form,
  FormField,
  ModalDialog,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import Table from "../../../../components/ui/Table";
import { getDeleteAction } from "../../../../utils/rowActions";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminLockAccessory } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { SearchAdminItem } from "../../../../http/types/items";
import { useItemsApi } from "../../../../http/useItems";
import { PaginationQuery } from "../../../../types";

type ItemOption = Pick<SearchAdminItem, "id" | "code" | "description">;

interface FormBody {
  lockItem: ItemOption | null;
  accessoryItem: ItemOption | null;
}

const AdminLockAccessoriesTab: FC = () => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "connectorAccessories" });
  const aborter = useRef(new AbortController());
  const debouncedValue = filters.state.q;
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = adminApi.useLockAccessories(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const [itemToDelete, setItemToDelete] = useState<AdminLockAccessory>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminLockAccessory>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}><Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.connectorAccessories.connector}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.connectorAccessories.accessorySku}</TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{item.lock.item.code}</TableCell>
              <TableCell>{item.accessory.code}</TableCell>
            </>
          )}
          rowActions={(item) => [getDeleteAction(i18n, () => setItemToDelete(item), true)]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.lockCalculatorModule.admin.connectorAccessories.singular}
            isSubmitting={isSubmitting}
            buttons={[<Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteLockAccessory(itemToDelete.id)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}>{i18n.translation.common.yes}</Button>]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <FormModal onClose={() => setIsCreateOpen(false)} />}
    </>
  );
};

const FormModal: FC<{ onClose: () => void }> = ({ onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();
  const itemsApi = useItemsApi();
  const [defaultOptions, setDefaultOptions] = useState<ItemOption[]>([]);
  const aborter = useRef(new AbortController());

  useEffect(() => {
    itemsApi
      .getAdminItems({ limit: 5, q: "" }, aborter.current.signal)
      .then((items) => setDefaultOptions(items.map((x) => ({ id: x.id, code: x.code, description: x.description }))))
      .catch(() => setDefaultOptions([]));
  }, []);

  const loadOptions = async (inputValue: string): Promise<ItemOption[]> => {
    aborter.current.abort();
    aborter.current = new AbortController();
    const items = await itemsApi.getAdminItems({ limit: 5, q: inputValue }, aborter.current.signal);
    return items.map((x) => ({ id: x.id, code: x.code, description: x.description }));
  };

  const defaultValues: FormBody = {
    lockItem: null,
    accessoryItem: null,
  };

  const onSubmit = () => async (formData: FormBody) => {
    if (!formData.lockItem || !formData.accessoryItem) return;
    try {
      await adminApi.insertLockAccessory({
        lockItemId: formData.lockItem.id,
        accessoryItemId: formData.accessoryItem.id,
      });
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={`${i18n.translation.common.add} ${i18n.translation.lockCalculatorModule.admin.connectorAccessories.singular}`}
    >
      <Form<FormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
       
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="lockItem"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.connectorAccessories.connector}
                  error={fieldState.error}
                 
                >
                  {({ labelId }) => (
                    <AsyncSelect<ItemOption, false>
                      {...field}
                      inputId={labelId}
                      defaultOptions={defaultOptions}
                      loadOptions={loadOptions}
                      isMulti={false}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(x) => (x.description ? `${x.code} - ${x.description}` : x.code)}
                      getOptionValue={(x) => x.id}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="accessoryItem"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.connectorAccessories.accessorySku}
                  error={fieldState.error}
                 
                >
                  {({ labelId }) => (
                    <AsyncSelect<ItemOption, false>
                      {...field}
                      inputId={labelId}
                      defaultOptions={defaultOptions}
                      loadOptions={loadOptions}
                      isMulti={false}
                      placeholder={`${i18n.translation.common.select}...`}
                      getOptionLabel={(x) => (x.description ? `${x.code} - ${x.description}` : x.code)}
                      getOptionValue={(x) => x.id}
                      isInvalid={fieldState.invalid}
                      onChange={(option) => field.onChange(option)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default AdminLockAccessoriesTab;
